import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { confetti, ConfettiConfig } from 'dom-confetti';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AppBarTitleService, ApplicationBaseData, ApplicationOfferBundleData, getBestOfferBundles, getMaxOfferValue, getOfferTermLabel, isLargeLoanProgramOffer, OfferType, ProductCode, ProductCodeLabel } from 'common';
import { AppSettings } from '../../../app.settings';
import { ApplicationService } from '../../shared/application.service';
import { CurrencyPipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { offerPageScreenFor, OfferPageScreen } from '../offer-page-status/offer-page-status.model';

@UntilDestroy()
@Component({
    selector: 'ifa-summary-calculator',
    templateUrl: './summary-calculator.component.html',
    styleUrls: ['./../base/base-calculator.component.scss', './summary-calculator.component.scss'],
    standalone: false
})
export class SummaryCalculatorComponent implements OnInit {
  @ViewChild('confettiCanvas', { static: true }) confettiCanvas: ElementRef;

  readonly OfferPageScreen = OfferPageScreen;

  // Left undefined until the application response lands, so the template renders neither the
  // offer body nor a message screen during the round-trip.
  offerPageScreen: OfferPageScreen;

  brandingCss: SafeResourceUrl;
  application: ApplicationBaseData;
  bundles: ApplicationOfferBundleData[];
  largeLoanProgramOffered: boolean;
  maxOfferAmount: number;
  maxRepaymentLabel: string;
  uuid: string;
  isPreview: boolean;
  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _sanitizer: DomSanitizer,
    private settings: AppSettings,
    private _applicationService: ApplicationService,
    private currencyPipe: CurrencyPipe,
    private _appBarTitleService: AppBarTitleService,
  ) {}

  ngOnInit() {
    this._route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.isPreview = data.preview;
    });

    this._route.queryParamMap.pipe(untilDestroyed(this)).subscribe((params) => {
      this.uuid = params.get('uuid');

      this._applicationService
        .getByUUID(this.uuid)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: this.dataInit.bind(this),
          error: (error) => {
            console.error(error);
            // Fail safe: the processing screen never reveals an offer.
            this.setOfferPageScreen(OfferPageScreen.Processing);
          },
        });

      let subdomain = null;

      if (!environment.production) subdomain = params.get('subdomain');

      if (!subdomain)
        // eslint-disable-next-line max-len
        this.brandingCss = this._sanitizer.bypassSecurityTrustResourceUrl(
          `${this.settings.brokers.url}/api/branding/loc-calculator-css`,
        );
      else
        // eslint-disable-next-line max-len
        this.brandingCss = this._sanitizer.bypassSecurityTrustResourceUrl(
          `${this.settings.brokers.url}/api/branding/loc-calculator-css?subdomain=${subdomain}`,
        );
    });
  }

  private dataInit(data: ApplicationBaseData) {
    this.setOfferPageScreen(offerPageScreenFor(data?.stage));
    this.application = data;
    this.bundles = this.getBestOffersByProductType(data);
    this.largeLoanProgramOffered = this.bundles.some(isLargeLoanProgramOffer);
    // Every offer, not just the cards: a card shows one offer per product.
    this.maxOfferAmount = getMaxOfferValue(data?.offerBundles, (x) => x.amount);
    this.maxRepaymentLabel = getOfferTermLabel(getMaxOfferValue(data?.offerBundles, (x) => x.repaymentTerm));

    // Only celebrate a real offer — previously this fired on a blind timer, whatever the stage.
    if (this.offerPageScreen === OfferPageScreen.FullOffer) this.showConfetti();
  }

  // Nothing titles the summary app bar today, so a message screen is the only case that sets one —
  // the approved page keeps its untitled bar. /calculator/summary has no title outlet at all.
  private setOfferPageScreen(screen: OfferPageScreen): void {
    this.offerPageScreen = screen;

    if (screen !== OfferPageScreen.FullOffer) this._appBarTitleService.title = 'Offers';
  }

  private showConfetti(): void {
    window.setTimeout(() => {
      confetti(this.confettiCanvas.nativeElement, <ConfettiConfig>{
        angle: 90,
        spread: 360,
        startVelocity: 50,
        elementCount: 100,
      });
    }, 1000);
  }

  getBestOffersByProductType(applicationData: ApplicationBaseData): ApplicationOfferBundleData[] {
    return getBestOfferBundles(applicationData?.offerBundles);
  }

  getOfferTypeByProductCode(product: ProductCode): OfferType {
    switch (product) {
      case ProductCode.Term:
        return OfferType.Term;
      case ProductCode.LOC:
        return OfferType.LOC;
      case ProductCode.Law:
        return OfferType.Law;
    }
  }

  get shareOfferEmailBody() {
    let customerUrlTree = this._router.createUrlTree(['preview/summary'], { queryParams: { uuid: this.uuid } });
    let shareUrl = window.location.origin + customerUrlTree.toString();

    return `Congratulations! ${this.application?.businessName} has offers for financing up to ${this.currencyPipe.transform(this.maxOfferAmount, 'USD', 'symbol', '1.2-2')} and terms up to ${this.maxRepaymentLabel}.
        See the offer details here:%0D%0A${encodeURIComponent(shareUrl)}`;
  }

  productCodeLabel(code: ProductCode): string {
    return ProductCodeLabel.get(code);
  }
}
